/**
 * TITAM AI - Google Ads - Página de Taxas
 * Taxas de conversão: ROAS, CTR, taxas de transição entre funil
 */

import { useState, useMemo } from 'react'
import { DataTable } from '../../components/DataTable.jsx'
import { MetricFunnel } from '../../components/Metrics.jsx'

// Função para calcular todas as taxas baseadas em dados REAIS
function enrichWithRates(data) {
  return data.map(item => {
    // Nova regra de negócio: Lead = Contato Efetivo + Formulário Preenchido
    const trueLeads = (item.contacts || 0) + (item.forms || 0)

    return {
      ...item,
      // ROAS (Retorno sobre o Investimento Publicitário)
      roas: item.cost > 0 ? (item.revenue || 0) / item.cost : 0,
      
      // Taxas do Funil de Conversão
      impToSale: item.impressions > 0 ? (item.sales || 0) / item.impressions * 100 : 0,
      ctr: item.impressions > 0 ? (item.clicks || 0) / item.impressions * 100 : 0,
      
      clickToVisit: item.clicks > 0 ? (item.visits || 0) / item.clicks * 100 : 0,
      
      // Ações do WhatsApp e Formulário
      visitToWhatsapp: item.visits > 0 ? (item.whatsappClicks || 0) / item.visits * 100 : 0,
      visitToForm: item.visits > 0 ? (item.forms || 0) / item.visits * 100 : 0,
      clickToContact: item.whatsappClicks > 0 ? (item.contacts || 0) / item.whatsappClicks * 100 : 0,
      
      visitToLead: item.visits > 0 ? trueLeads / item.visits * 100 : 0,
      leadToQualified: trueLeads > 0 ? (item.qualifiedLeads || 0) / trueLeads * 100 : 0,
      qualifiedToSale: item.qualifiedLeads > 0 ? (item.sales || 0) / item.qualifiedLeads * 100 : 0,
    }
  })
}

// Colunas para cada nível - Atualizado com Impressões -> Venda
const ACCOUNT_COLUMNS = [
  { key: 'name', label: 'Conta', sortable: true },
  { key: 'roas', label: 'ROAS', format: 'decimal', sortable: true, align: 'right' },
  { key: 'ctr', label: 'CTR', format: 'percent', sortable: true, align: 'right' },
  { key: 'clickToVisit', label: 'Click→Visita %', format: 'percent', sortable: true, align: 'right' },
  { key: 'visitToWhatsapp', label: 'Visita→Whats %', format: 'percent', sortable: true, align: 'right' },
  { key: 'clickToContact', label: 'Whats→Contato %', format: 'percent', sortable: true, align: 'right' },
  { key: 'visitToForm', label: 'Visita→Form %', format: 'percent', sortable: true, align: 'right' },
  { key: 'visitToLead', label: 'Visita→Lead %', format: 'percent', sortable: true, align: 'right' },
  { key: 'leadToQualified', label: 'Lead → Qualificado %', format: 'percent', sortable: true, align: 'right' },
  { key: 'qualifiedToSale', label: 'Qualificado → Venda %', format: 'percent', sortable: true, align: 'right' },
  { key: 'impToSale', label: 'Imp.→Venda %', format: 'percent', sortable: true, align: 'right' },
]

const CAMPAIGN_COLUMNS = [
  { key: 'name', label: 'Campanha', sortable: true },
  { key: 'accountName', label: 'Conta', sortable: true },
  { key: 'status', label: 'Status', type: 'badge', sortable: true },
  { key: 'roas', label: 'ROAS', format: 'decimal', sortable: true, align: 'right' },
  { key: 'ctr', label: 'CTR', format: 'percent', sortable: true, align: 'right' },
  { key: 'clickToVisit', label: 'Click→Visita %', format: 'percent', sortable: true, align: 'right' },
  { key: 'visitToWhatsapp', label: 'Visita→Whats %', format: 'percent', sortable: true, align: 'right' },
  { key: 'visitToForm', label: 'Visita→Form %', format: 'percent', sortable: true, align: 'right' },
  { key: 'visitToLead', label: 'Visita→Lead %', format: 'percent', sortable: true, align: 'right' },
  { key: 'leadToQualified', label: 'Lead → Qualificado %', format: 'percent', sortable: true, align: 'right' },
  { key: 'qualifiedToSale', label: 'Qualificado → Venda %', format: 'percent', sortable: true, align: 'right' },
  { key: 'impToSale', label: 'Imp.→Venda %', format: 'percent', sortable: true, align: 'right' },
]

const AD_GROUP_COLUMNS = [
  { key: 'name', label: 'Grupo de Anúncios', sortable: true },
  { key: 'campaignName', label: 'Campanha', sortable: true },
  { key: 'accountName', label: 'Conta', sortable: true },
  { key: 'ctr', label: 'CTR', format: 'percent', sortable: true, align: 'right' },
  { key: 'clickToVisit', label: 'Click→Visita %', format: 'percent', sortable: true, align: 'right' },
  { key: 'visitToLead', label: 'Visita→Lead %', format: 'percent', sortable: true, align: 'right' },
  { key: 'leadToQualified', label: 'Lead → Qualificado %', format: 'percent', sortable: true, align: 'right' },
  { key: 'impToSale', label: 'Imp.→Venda %', format: 'percent', sortable: true, align: 'right' },
]

const AD_COLUMNS = [
  { key: 'name', label: 'Anúncio', sortable: true },
  { key: 'groupName', label: 'Grupo', sortable: true },
  { key: 'campaignName', label: 'Campanha', sortable: true },
  { key: 'ctr', label: 'CTR', format: 'percent', sortable: true, align: 'right' },
  { key: 'clickToVisit', label: 'Click→Visita %', format: 'percent', sortable: true, align: 'right' },
  { key: 'visitToLead', label: 'Visita→Lead %', format: 'percent', sortable: true, align: 'right' },
  { key: 'leadToQualified', label: 'Lead → Qualificado %', format: 'percent', sortable: true, align: 'right' },
  { key: 'impToSale', label: 'Imp.→Venda %', format: 'percent', sortable: true, align: 'right' },
]

export default function RatesPage({ data, level, onDrillDown }) {
  const dataWithRates = useMemo(() => enrichWithRates(data), [data])

  // Calcular médias ponderadas para os cards
  const averages = useMemo(() => {
    const totals = data.reduce((acc, item) => ({
      cost: acc.cost + (item.cost || 0),
      impressions: acc.impressions + (item.impressions || 0),
      clicks: acc.clicks + (item.clicks || 0),
      visits: acc.visits + (item.visits || 0),
      whatsappClicks: acc.whatsappClicks + (item.whatsappClicks || 0),
      forms: acc.forms + (item.forms || 0),
      contacts: acc.contacts + (item.contacts || 0),
      qualifiedLeads: acc.qualifiedLeads + (item.qualifiedLeads || 0),
      sales: acc.sales + (item.sales || 0),
      revenue: acc.revenue + (item.revenue || 0),
    }), {
      cost: 0, impressions: 0, clicks: 0, visits: 0, whatsappClicks: 0,
      forms: 0, contacts: 0, qualifiedLeads: 0, sales: 0, revenue: 0
    })

    // Aplicando a nova regra também nos totais globais
    const trueTotalLeads = totals.contacts + totals.forms

    return {
      roas: totals.cost > 0 ? totals.revenue / totals.cost : 0,
      ctr: totals.impressions > 0 ? (totals.clicks / totals.impressions) * 100 : 0,
      clickToVisit: totals.clicks > 0 ? (totals.visits / totals.clicks) * 100 : 0,
      visitToWhatsapp: totals.visits > 0 ? (totals.whatsappClicks / totals.visits) * 100 : 0,
      visitToLead: totals.visits > 0 ? (trueTotalLeads / totals.visits) * 100 : 0,
      leadToQualified: trueTotalLeads > 0 ? (totals.qualifiedLeads / trueTotalLeads) * 100 : 0,
      qualifiedToSale: totals.qualifiedLeads > 0 ? (totals.sales / totals.qualifiedLeads) * 100 : 0,
      impToSale: totals.impressions > 0 ? (totals.sales / totals.impressions) * 100 : 0,
    }
  }, [data])

  // Selecionar colunas baseado no nível
  const columns = useMemo(() => {
    switch (level) {
      case 'account': return ACCOUNT_COLUMNS
      case 'campaign': return CAMPAIGN_COLUMNS
      case 'adgroup': return AD_GROUP_COLUMNS
      case 'ad': return AD_COLUMNS
      default: return ACCOUNT_COLUMNS
    }
  }, [level])

  return (
    <div className="ga-page">
      {/* Cards de Taxas Principais — mesmo visual de funil da Quantitativa */}
      <MetricFunnel
        rows={[[
          { label: 'ROAS Médio', value: averages.roas, format: 'decimal' },
          { label: 'CTR Médio', value: averages.ctr, format: 'percent' },
          { label: 'Click → Visita', value: averages.clickToVisit, format: 'percent' },
          { label: 'Visita → Whats', value: averages.visitToWhatsapp, format: 'percent' },
          { label: 'Lead → Qualificado', value: averages.leadToQualified, format: 'percent' },
          { label: 'Qualificado → Venda', value: averages.qualifiedToSale, format: 'percent' },
          { label: 'Imp. → Venda', value: averages.impToSale, format: 'percent' },
        ]]}
      />

      {/* Tabela */}
      <div className="table-section">
        <div className="table-section-header">
          <div>
            <div className="table-section-title">
              Taxas de Conversão
            </div>
            <div className="table-section-subtitle">
              Clique no cabeçalho da coluna para ordenar • Todas as taxas calculadas automaticamente com dados reais do CRM
            </div>
          </div>
        </div>
        <DataTable
          columns={columns}
          data={dataWithRates}
          hoverable
          striped
          scrollable
          onRowClick={onDrillDown}
          emptyText="Nenhum dado encontrado para os filtros selecionados"
        />
      </div>
    </div>
  )
}